import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Grid,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  PeopleAltOutlined,
  Share,
  ContentCopy,
  Diamond,
  ArrowBack,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationOnIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import BottomNav from '../components/BottomNavigation';
import BrandLoader from '../components/BrandLoader';
import useResponsive from '../hooks/useResponsive';
import { QRCodeSVG } from 'qrcode.react';
import { referralAPI } from '../services/api';
import { formatCurrency } from '../utils/countryUtils';
import { formatDateShort } from '../utils/timezoneUtils';
import './ReferFriend.css';

// Styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
}));

// Tab Panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`referral-tabpanel-${index}`}
      aria-labelledby={`referral-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ReferEarn = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [referralAnalytics, setReferralAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Dialog states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalReferrals, setTotalReferrals] = useState(0);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    loadReferralData();
  }, []);

  useEffect(() => {
    loadReferralHistory();
  }, [page, rowsPerPage, statusFilter]);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      const [info, analytics] = await Promise.all([
        referralAPI.getReferralInfo(),
        referralAPI.getReferralAnalytics()
      ]);
      
      // Get referral data from backend
      const referralData = info.data || info;
      
      // Generate referral link on frontend using customer app domain
      const customerAppUrl = 'https://customer.nistylez.com';
      const referralLinkWithCorrectDomain = `${customerAppUrl}/signup?ref=${referralData.referralCode}`;
      
      // Override backend URL with frontend-generated URL
      setReferralInfo({
        ...referralData,
        referralLink: referralLinkWithCorrectDomain
      });
      setReferralAnalytics(analytics.data || analytics);
    } catch (error) {
      showSnackbar('Error loading referral data', 'error');
      // Set fallback data
      const fallbackCode = `REF${user?.id?.toString().slice(-6).toUpperCase() || Math.random().toString(36).substr(2, 6).toUpperCase()}`;
      setReferralInfo({
        referralCode: fallbackCode,
        referralLink: `https://customer.nistylez.com/signup?ref=${fallbackCode}`,
        stats: {
          totalReferrals: 0,
          successfulReferrals: 0,
          pendingReferrals: 0,
          totalGemsEarned: 0,
          totalCashEarned: 0
        },
        activeReferrals: 0,
        settings: {
          customerReferCustomer: 200,
          customerReferProvider: 300,
          newCustomerSignup: 100,
          firstOrderBonus: 50,
          firstBookingBonus: 75
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const loadReferralHistory = async () => {
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await referralAPI.getReferralHistory(params);
      const data = response.data || response;
      setReferralHistory(data.referrals || []);
      setTotalReferrals(data.pagination?.total || 0);
    } catch (error) {
      showSnackbar('Error loading referral history', 'error');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCopyReferralLink = async () => {
    if (!referralInfo) return;

    try {
      await navigator.clipboard.writeText(referralInfo.referralLink);
      showSnackbar('Referral link copied to clipboard!', 'success');
    } catch (error) {
      showSnackbar('Failed to copy referral link', 'error');
    }
  };

  const handleGenerateNewCode = async () => {
    try {
      const response = await referralAPI.generateReferralCode();
      const data = response.data || response;
      
      // Generate referral link on frontend using customer app domain
      const customerAppUrl = 'https://customer.nistylez.com';
      const referralLinkWithCorrectDomain = `${customerAppUrl}/signup?ref=${data.referralCode}`;
      
      setReferralInfo(prev => prev ? {
        ...prev,
        referralCode: data.referralCode,
        referralLink: referralLinkWithCorrectDomain
      } : null);
      showSnackbar('New referral code generated!', 'success');
    } catch (error) {
      showSnackbar('Error generating referral code', 'error');
    }
  };

  const handleShareViaEmail = async () => {
    if (!shareEmail.trim()) {
      showSnackbar('Please enter an email address', 'error');
      return;
    }

    try {
      await referralAPI.shareReferral({
        email: shareEmail,
        message: shareMessage
      });
      setEmailDialogOpen(false);
      setShareEmail('');
      setShareMessage('');
      showSnackbar('Referral email sent successfully!', 'success');
    } catch (error) {
      showSnackbar('Error sending referral email', 'error');
    }
  };

  const handleShareViaWhatsApp = () => {
    if (!referralInfo) return;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`Use my referral code ${referralInfo.referralCode} to sign up and get special bonuses! ${referralInfo.referralLink}`)}`;
    window.open(whatsappUrl, '_blank');
    setShareDialogOpen(false);
  };

  const handleShareViaSMS = () => {
    if (!referralInfo) return;
    const smsUrl = `sms:?body=${encodeURIComponent(`Use my referral code ${referralInfo.referralCode} to sign up and get special bonuses! ${referralInfo.referralLink}`)}`;
    window.open(smsUrl, '_blank');
    setShareDialogOpen(false);
  };

  const handleShareViaEmailApp = () => {
    if (!referralInfo) return;
    const emailUrl = `mailto:${shareEmail}?subject=Join me on NISTYLEZ&body=${encodeURIComponent(`${shareMessage}\n\nUse my referral code ${referralInfo.referralCode} to sign up and get special bonuses!\n\n${referralInfo.referralLink}`)}`;
    window.open(emailUrl, '_blank');
    setShareDialogOpen(false);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return formatDateShort(dateString);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <BrandLoader />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f5f5',
      pb: 8 // Add padding bottom for bottom navigation
    }}>
      {/* Dark Blue Header with Back Arrow */}
      <StyledAppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Refer & Earn
          </Typography>
        </Toolbar>
      </StyledAppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Invite friends and earn rewards! Share your referral code and start earning gems and cash rewards.
        </Typography>

        {/* Statistics Cards */}
        {referralInfo && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  transition: 'transform 0.3s ease-in-out',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <PeopleAltOutlined sx={{ mr: 2, color: 'white' }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                        {referralInfo.stats.totalReferrals}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        Total Referrals
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  transition: 'transform 0.3s ease-in-out',
                  boxShadow: '0 8px 25px rgba(240, 147, 251, 0.3)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <TrendingUpIcon sx={{ mr: 2, color: 'white' }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                        {referralInfo.stats.successfulReferrals}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        Successful
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  transition: 'transform 0.3s ease-in-out',
                  boxShadow: '0 8px 25px rgba(79, 172, 254, 0.3)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Diamond sx={{ mr: 2, color: 'white' }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                        {referralInfo.stats.totalGemsEarned}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        Gems Earned
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Main Content Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="referral tabs">
            <Tab label="Overview" />
            <Tab label="History" />
            <Tab label="Analytics" />
          </Tabs>

          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            {referralInfo && (
              <Grid container spacing={3}>
                {/* Referral Code Card */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Your Referral Code
                      </Typography>
                      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          value={referralInfo.referralCode}
                          InputProps={{ readOnly: true }}
                          sx={{ mr: 1 }}
                        />
                        <IconButton onClick={handleCopyReferralLink} color="primary">
                          <ContentCopy />
                        </IconButton>
                      </Box>
                      {/* Generate New Code button hidden to maintain persistent referral codes for tracking */}
                      <Button
                        variant="contained"
                        startIcon={<Share />}
                        onClick={() => setShareDialogOpen(true)}
                        fullWidth
                      >
                        Share
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* QR Code Card */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        QR Code
                      </Typography>
                      <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                        <Box sx={{ 
                          p: 2, 
                          border: '2px solid #e0e0e0', 
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }}>
                          <QRCodeSVG 
                            value={referralInfo.referralLink}
                            size={150}
                            level="M"
                            includeMargin={true}
                          />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        Scan this QR code to access your referral link
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Referral Link Card */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Your Referral Link
                      </Typography>
                      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          value={referralInfo.referralLink}
                          InputProps={{ readOnly: true }}
                          sx={{ mr: 1 }}
                        />
                        <IconButton onClick={handleCopyReferralLink} color="primary">
                          <ContentCopy />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Share this link with friends to start earning rewards!
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Rewards Card */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
                        Rewards Structure
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Card sx={{ 
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              transition: 'transform 0.3s ease-in-out',
                              boxShadow: '0 6px 20px rgba(240, 147, 251, 0.3)'
                            }
                          }}>
                            <CardContent>
                              <Box textAlign="center" p={2}>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
                                  {referralInfo.settings.customerReferCustomer}
                                </Typography>
                                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>
                                  Gems for Customer Referral
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                                  Earn gems when customers sign up using your code
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card sx={{ 
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              transition: 'transform 0.3s ease-in-out',
                              boxShadow: '0 6px 20px rgba(79, 172, 254, 0.3)'
                            }
                          }}>
                            <CardContent>
                              <Box textAlign="center" p={2}>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
                                  {referralInfo.settings.customerReferProvider}
                                </Typography>
                                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>
                                  Gems for Provider Referral
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                                  Earn gems when providers join using your code
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* History Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Status Filter</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status Filter"
                      onChange={handleStatusFilterChange}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <IconButton onClick={loadReferralHistory}>
                    <RefreshIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Referred User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Gems Reward</TableCell>
                    <TableCell>Cash Reward</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Completed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referralHistory.map((referral) => (
                    <TableRow key={referral._id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                            {referral.referredUser?.firstName?.charAt(0) || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {referral.referredUser?.firstName} {referral.referredUser?.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {referral.referredUser?.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={referral.referredUserType}
                          size="small"
                          color={referral.referredUserType === 'provider' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={referral.status}
                          size="small"
                          color={getStatusColor(referral.status)}
                        />
                      </TableCell>
                      <TableCell>{referral.gemsReward} gems</TableCell>
                      <TableCell>{formatCurrency(referral.rewardAmount || 0)}</TableCell>
                      <TableCell>{formatDate(referral.createdAt)}</TableCell>
                      <TableCell>
                        {referral.completedAt ? formatDate(referral.completedAt) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalReferrals}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </TabPanel>

          {/* Analytics Tab */}
          <TabPanel value={tabValue} index={2}>
            {referralAnalytics && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Performance Overview (30 days)
                      </Typography>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary="Total Referrals"
                            secondary={referralAnalytics.summary?.totalReferrals || 0}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Successful Referrals"
                            secondary={referralAnalytics.summary?.totalCompleted || 0}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Conversion Rate"
                            secondary={`${referralAnalytics.summary?.conversionRate || 0}%`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Total Gems Earned"
                            secondary={`${referralAnalytics.summary?.totalGemsEarned || 0} gems`}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Earnings Summary (30 days)
                      </Typography>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary="Total Gems Earned"
                            secondary={`${referralAnalytics.summary?.totalGemsEarned || 0} gems`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Total Cash Earned"
                            secondary={formatCurrency(referralAnalytics.summary?.totalCashEarned || 0)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Average per Referral"
                            secondary={`${referralAnalytics.summary?.totalReferrals > 0 ? Math.round(referralAnalytics.summary.totalGemsEarned / referralAnalytics.summary.totalReferrals) : 0} gems`}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>
        </Paper>

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Share Your Referral Link</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Choose how you'd like to share your referral link:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => {
                    setShareDialogOpen(false);
                    setEmailDialogOpen(true);
                  }}
                >
                  Email
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  onClick={handleShareViaWhatsApp}
                >
                  WhatsApp
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<SmsIcon />}
                  onClick={handleShareViaSMS}
                >
                  SMS
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyReferralLink}
                >
                  Copy Link
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Email Share Dialog */}
        <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Share via Email</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              label="Message (Optional)"
              multiline
              rows={4}
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder="I thought you might be interested in joining NISTYLEZ!"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleShareViaEmailApp} variant="outlined">
              Open Email App
            </Button>
            <Button onClick={handleShareViaEmail} variant="contained">
              Send Email
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default ReferEarn;
